import {
  Drawer,
  drawerClasses,
  styled,
  tableBodyClasses,
  tableCellClasses,
  tableClasses,
  tableHeadClasses,
  tableRowClasses,
} from '@mui/material';

import { MAX_DRAWER_WIDTH, ORGAN_COLORS } from '#shared';

const PREFIX = 'TSMED-hubsReport-CellDrawer';

export const cellDrawerClasses = {
  header: `${PREFIX}-header`,
  headerPrimary: `${PREFIX}-headerPrimary`,
  headerPrimaryIcon: `${PREFIX}-headerPrimaryIcon`,
  headerPrimaryTitle: `${PREFIX}-headerPrimaryTitle`,
  headerPrimaryClose: `${PREFIX}-headerPrimaryClose`,
  headerSecondary: `${PREFIX}-headerSecondary`,
  headerSecondaryIcon: `${PREFIX}-headerSecondaryIcon`,
  headerSecondaryLocation: `${PREFIX}-headerSecondaryLocation`,
  headerSecondaryText: `${PREFIX}-headerSecondaryText`,
  headerSecondaryDay: `${PREFIX}-headerSecondaryDay`,
  resourceTableContainer: `${PREFIX}-resourceTableContainer`,
  counts: `${PREFIX}-counts`,
  countBlock: `${PREFIX}-countBlock`,
  countsIcon: `${PREFIX}-countsIcon`,
  countsNumber: `${PREFIX}-countsNumber`,
  countsText: `${PREFIX}-countsText`,
  humanResources: `${PREFIX}-humanResources`,
  deviceResources: `${PREFIX}-deviceResources`,
  resourceName: `${PREFIX}-resourcesName`,
  resourceOnRest: `${PREFIX}-resourceOnRest`,
  resourceStatus: `${PREFIX}-resourcesStatus`,
  resourceScheduleSubType: `${PREFIX}-resourceScheduleSubType`,
  resourceAssignedTo: `${PREFIX}-resourceAssignedTo`,
  resourceSerialNumber: `${PREFIX}-resourceTableSerialNumber`,
  resourceDateTime: `${PREFIX}-resourceDateTime`,
  resourceDate: `${PREFIX}-resourceDate`,
  resourceTime: `${PREFIX}-resourceTime`,
  resourceReturnTime: `${PREFIX}-resourceReturnTime`,
  resourceReturningTable: `${PREFIX}-resourceReturningTable`,
  available: `${PREFIX}-available`,
  unavailable: `${PREFIX}-unavailable`,
  assigned: `${PREFIX}-assigned`,
  resourceTrial: `${PREFIX}-resourceTrial`,
};

export const CellDrawerRoot = styled(Drawer, {
  name: PREFIX,
  overridesResolver: (props, styles) => styles.root,
})(({ theme: { palette, spacing, breakpoints, utils, mixins } }) => ({
  [`.${drawerClasses.paper}`]: {
    display: 'flex',
    flexDirection: 'column',
    padding: spacing(3),
    gap: spacing(4),
    background: '#1F2B37',
    width: MAX_DRAWER_WIDTH,
    maxWidth: '100vw',
    boxSizing: 'border-box',
    overflow: 'auto',

    [breakpoints.down('lg')]: {
      gap: spacing(4),
      padding: spacing(2),
    },

    [breakpoints.down('sm')]: {
      width: '100vw',
    },
  },
  [`.${cellDrawerClasses.header}`]: {
    display: 'flex',
    gap: spacing(2),
    flexDirection: 'column',

    [breakpoints.down('xl')]: {
      gap: spacing(1),
    },
  },

  [`.${cellDrawerClasses.headerPrimary}`]: {
    display: 'flex',
    gap: spacing(2),
    alignItems: 'center',

    [breakpoints.down('xl')]: {
      gap: spacing(1),
    },
  },
  [`.${cellDrawerClasses.headerPrimaryIcon}`]: {
    width: utils.clamp(20, 32),
    height: utils.clamp(20, 32),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 4,
    [`&.Liver`]: {
      background: ORGAN_COLORS.Liver,
    },
    [`&.Lungs`]: {
      background: ORGAN_COLORS.Lungs,
    },
    [`&.Heart`]: {
      background: ORGAN_COLORS.Heart,
    },
    svg: {
      width: utils.clamp(14, 18),
      height: utils.clamp(14, 18),
      color: 'black',
    },
  },
  [`.${cellDrawerClasses.headerPrimaryTitle}`]: {
    fontSize: utils.clamp(16, 32),
    color: '#FFFFFF',
    fontWeight: 600,
    flexGrow: 1,
  },
  [`.${cellDrawerClasses.headerPrimaryClose}`]: {
    display: 'flex',
    cursor: 'pointer',
    svg: {
      width: utils.clamp(16, 25),
      height: utils.clamp(16, 25),
      fill: '#FFFFFF',
    },
  },
  [`.${cellDrawerClasses.headerSecondary}`]: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing(2),

    [breakpoints.down('xl')]: {
      gap: spacing(1),
    },
  },
  [`.${cellDrawerClasses.headerSecondaryLocation}`]: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    fontSize: utils.clamp(14, 16),
    color: palette.grey[200],
  },
  [`.${cellDrawerClasses.headerSecondaryIcon}`]: {
    display: 'flex',
    svg: {
      width: utils.clamp(14, 18),
      height: utils.clamp(14, 18),
      fill: palette.grey[200],
    },
  },
  [`.${cellDrawerClasses.headerSecondaryText}`]: {
    textTransform: 'capitalize',
  },
  [`.${cellDrawerClasses.headerSecondaryDay}`]: {
    background: '#364B5F',
    lineHeight: '24px',
    display: 'block',
    fontSize: utils.clamp(10, 12),
    borderRadius: 24,
    padding: '0 12px',
    textTransform: 'capitalize',
    fontWeight: 600,
    color: palette.grey[200],
  },
  [`.${cellDrawerClasses.resourceTableContainer}`]: {
    ...mixins.organStyles(palette),
    display: 'flex',
    gap: spacing(2),
    flexDirection: 'column',
    paddingBottom: spacing(6),
    [breakpoints.down('xl')]: {
      gap: spacing(1),
    },
  },
  [`.${cellDrawerClasses.counts}`]: {
    display: 'flex',
    gap: spacing(2),

    [breakpoints.down('xl')]: {
      gap: spacing(1),
    },
  },
  [`.${cellDrawerClasses.countBlock}`]: {
    flexGrow: 1,
    border: '1px solid',
    display: 'flex',
    padding: spacing(1.5, 2),
    gap: spacing(1),
    fontSize: utils.clamp(14, 20),
    alignItems: 'center',
    borderRadius: 4,
    [`&.${cellDrawerClasses.available}`]: {
      background: 'rgba(131, 188, 98, 0.1)',
    },
    [`&.${cellDrawerClasses.unavailable}`]: {
      background: 'rgba(182, 182, 182, 0.1)',
    },
    [`&.${cellDrawerClasses.assigned}`]: {
      background: 'rgba(187, 52, 64, 0.1)',
    },

    [breakpoints.down('xl')]: {
      padding: spacing(1, 1.5),
    },
  },
  [`.${cellDrawerClasses.countsIcon}`]: {
    svg: {
      display: 'block',
      width: utils.clamp(14, 20),
      height: utils.clamp(14, 20),
    },
  },
  [`.${cellDrawerClasses.countsNumber}`]: {},
  [`.${cellDrawerClasses.countsText}`]: {},
  [`.${tableClasses.root}`]: {
    background: '#141F29',
    borderRadius: 4,
    border: '1px solid #364B5F',
    display: 'table',

    [breakpoints.down('sm')]: {
      display: 'block',
      overflowX: 'auto',
      WebkitOverflowScrolling: 'touch',
    },

    [`.${tableCellClasses.root}`]: {
      whiteSpace: 'nowrap',
      boxSizing: 'border-box',
    },
    [`.${tableHeadClasses.root}`]: {
      whiteSpace: 'nowrap',

      [`.${tableRowClasses.root}`]: {
        [`.${tableCellClasses.root}`]: {
          whiteSpace: 'nowrap',
          fontSize: utils.clamp(10, 12),
          fontWeight: 500,
          color: '#7591AD',
          borderBottom: '1px solid #364B5F',
          padding: '4px 8px',
          lineHeight: '16px',
        },
      },
    },
    [`.${tableBodyClasses.root}`]: {},
    [`.${tableRowClasses.root}`]: {
      ['&.odd']: {
        background: '#1C2733',
      },
    },
  },
  [`.${tableBodyClasses.root}`]: {
    [`.${tableCellClasses.root}`]: {
      whiteSpace: 'nowrap',
      borderBottom: 'none',
      color: palette.grey[200],
      padding: spacing(2, 1),
      fontSize: utils.clamp(10, 14),
      lineHeight: utils.clamp(14, 20),

      [breakpoints.down('xl')]: {
        padding: spacing(1.5, 1),
      },
    },
  },
  [`.${cellDrawerClasses.humanResources}`]: {
    overflowY: 'auto',
    flexGrow: 1,
    minHeight: 0,
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',

    '&::-webkit-scrollbar': {
      display: 'none',
    },

    [`.${cellDrawerClasses.resourceOnRest}`]: {
      [`.${cellDrawerClasses.resourceName}`]: {
        [`.inner`]: {
          background: palette.mapBlue[500],
          padding: spacing(0.5),
          borderRadius: spacing(1),
        },
      },
    },
    [`.${tableCellClasses.root}`]: {
      whiteSpace: 'nowrap',

      [`&.${cellDrawerClasses.resourceName}`]: {
        width: 150,
      },
      [`&.${cellDrawerClasses.resourceScheduleSubType}`]: {
        width: 96.5,
        maxWidth: 96.5,
      },
      [`&.${cellDrawerClasses.resourceDateTime}`]: {
        textAlign: 'right',
        width: 84,
        [`.${cellDrawerClasses.resourceDate}`]: {
          fontSize: utils.clamp(10, 14),
        },
        [`.${cellDrawerClasses.resourceTime}`]: {
          fontSize: utils.clamp(10, 12),
          color: '#7F8083',
          fontWeight: 400,
        },
      },
      [`&.${cellDrawerClasses.resourceStatus}`]: {
        whiteSpace: 'nowrap',
        width: 92,
        span: {
          display: 'block',
          color: '#ffffff',
          padding: '4px 12px',
          borderRadius: 4,
          fontSize: utils.clamp(10, 12),
        },
      },
      [`&.${cellDrawerClasses.resourceAssignedTo}`]: {
        color: 'var(--primaryColor)',
        width: 93,
        textAlign: 'right',
      },
      [`&.${cellDrawerClasses.resourceReturnTime}`]: {
        width: 80,
        textAlign: 'right',
      },
    },
  },
  [`.${cellDrawerClasses.deviceResources}`]: {
    overflowY: 'auto',
    flexGrow: 1,
    minHeight: 0,

    [`.${tableCellClasses.root}`]: {
      [`&.${cellDrawerClasses.resourceName}`]: {
        whiteSpace: 'nowrap',
        width: 285,
      },
      [`&.${cellDrawerClasses.resourceScheduleSubType}`]: {
        width: 96.5,
        maxWidth: 96.5,
      },
      [`&.${cellDrawerClasses.resourceStatus}`]: {
        width: 92,
        span: {
          display: 'block',
          color: '#ffffff',
          padding: '4px 12px',
          borderRadius: 4,
          fontSize: utils.clamp(10, 12),
          fontWeight: 500,
        },
      },
      [`&.${cellDrawerClasses.resourceSerialNumber}`]: {
        width: 130,
        textAlign: 'right',
      },
      [`&.${cellDrawerClasses.resourceAssignedTo}`]: {
        color: 'var(--primaryColor)',
        width: 93,
        textAlign: 'right',
      },
      [`&.${cellDrawerClasses.resourceTrial}`]: {
        width: 92,
      },
    },
  },
  [`.${cellDrawerClasses.resourceReturningTable}`]: {
    gap: 16,
    display: 'flex',
    flexDirection: 'column',
    [`.${cellDrawerClasses.headerPrimaryTitle}`]: {
      fontSize: utils.clamp(14, 24),
      fontWeight: 500,
    },
    [`.${tableCellClasses.root}`]: {
      whiteSpace: 'nowrap',

      [`&.${cellDrawerClasses.resourceName}`]: {
        width: 230,
      },
      [`&.${cellDrawerClasses.resourceScheduleSubType}`]: {
        width: 184.5,
        maxWidth: 'none',
      },
      [`&.${cellDrawerClasses.resourceStatus}`]: {
        width: 85,
        span: {
          background: '#364B5F',
        },
      },
      [`&.${cellDrawerClasses.resourceReturnTime}`]: {
        width: 99,
        textAlign: 'right',
        fontSize: utils.clamp(10, 14),
        fontWeight: 400,
      },
    },
  },
  [`.${cellDrawerClasses.available}`]: {
    color: '#83BC62',
    background: '#83BC62',
  },
  [`.${cellDrawerClasses.unavailable}`]: {
    color: '#7F8083',
    background: '#7F8083',
  },
  [`.${cellDrawerClasses.assigned}`]: {
    color: '#FF4848',
    background: '#FF4848',
  },
}));
